import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
} from "@mui/material";
import {
  People as PeopleIcon,
  Class as ClassIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import StatCard from "../components/infographics/StatCard";
import { useAuth } from "../contexts/AuthContext";

const TrainerDashboard = () => {
  const theme = useTheme();
  const { user } = useAuth();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <Box>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <motion.div variants={itemVariants}>
          <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Trainer Dashboard
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Welcome back, {user?.name || user?.user_name || "Trainer"}! Manage your training sessions and students.
              </Typography>
            </Box>
            <Chip
              label="Trainer"
              color="secondary"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </motion.div>

        {/* Statistics Cards */}
        <motion.div variants={itemVariants}>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="My Students"
                value={89}
                icon={<PeopleIcon />}
                color="primary"
                trend="+5 this month"
                trendColor="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Training Sessions"
                value={18}
                icon={<ClassIcon />}
                color="secondary"
                trend="3 this week"
                trendColor="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Attendance Rate"
                value={91.2}
                icon={<CheckCircleIcon />}
                color="success"
                trend="+1.8% this week"
                trendColor="success"
                suffix="%"
                decimals={1}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Upcoming Sessions"
                value={6}
                icon={<ScheduleIcon />}
                color="warning"
                trend="Next: Tomorrow"
                trendColor="info"
              />
            </Grid>
          </Grid>
        </motion.div>

        {/* Training Overview */}
        <motion.div variants={itemVariants}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Training Session Overview
                  </Typography>
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <ClassIcon sx={{ fontSize: 64, color: "secondary.main", mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      Active Training Programs
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                      Manage your training sessions and track student progress
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Quick Actions
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {[
                      { label: "Start Session", icon: <ClassIcon />, color: "primary" },
                      { label: "Mark Attendance", icon: <CheckCircleIcon />, color: "success" },
                      { label: "View Students", icon: <PeopleIcon />, color: "secondary" },
                      { label: "Schedule Session", icon: <ScheduleIcon />, color: "info" },
                    ].map((action, index) => (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Card
                          sx={{
                            p: 2,
                            cursor: "pointer",
                            border: `1px solid ${theme.palette.divider}`,
                            "&:hover": {
                              borderColor: `${action.color}.main`,
                              bgcolor: `${action.color}.light` + "10",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                bgcolor: `${action.color}.light`,
                                color: `${action.color}.main`,
                              }}
                            >
                              {action.icon}
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {action.label}
                            </Typography>
                          </Box>
                        </Card>
                      </motion.div>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </motion.div>
      </motion.div>
    </Box>
  );
};

export default TrainerDashboard;

